'use client'

import { motion } from 'framer-motion'
import { ClipboardList, Plane, Headset } from 'lucide-react'
import { useState, useEffect } from 'react'

interface ProcessStep {
  icon: typeof ClipboardList
  title: string
  description: string
}

const processSteps: ProcessStep[] = [
  {
    icon: ClipboardList,
    title: 'Consultation & Assessment',
    description: 'We collaborate with you to understand your aviation needs and develop a tailored leasing solution.'
  },
  {
    icon: Plane,
    title: 'Aircraft Selection & Setup',
    description: 'Our team matches you with the perfect aircraft and handles all documentation.'
  },
  {
    icon: Headset,
    title: 'Deployment & Support',
    description: 'We facilitate the pick up or delivery of your aircraft and provide ongoing support and assistance throughout the term of your lease.'
  }
]

export default function AnimatedProcessSection() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    // Check user's motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Stagger container variants
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  }

  // Card variants with fade + slide up
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 24 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1] as const // easeOut cubic bezier
      }
    }
  }


  return (
    <>
      {/* Process Section */}
      <section className="py-6 sm:py-8 lg:py-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-3xl text-center mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl mb-1">
              Transform Your Aviation Operations In Three Easy Steps
            </h2>
          </motion.div>

          <motion.div 
            className="mx-auto grid max-w-6xl grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {processSteps.map((step, index) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  className="text-center bg-white rounded-xl border border-gray-200 p-5 sm:p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
                  tabIndex={0}
                  role="article"
                  aria-label={step.title}
                >
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-[#0A2342] transition-colors">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>
    </>
  )
}

